import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Calendar, User } from "lucide-react";
import { Link } from "react-router-dom";
import { fileUrl } from "../lib/api";

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch {
    return "";
  }
}

function formatTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}

const NewsCard = React.forwardRef(function NewsCard(
  { article, depth = 0, dragHandlers, style, animate, initial, exit, transition },
  ref
) {
  return (
    <motion.article
      ref={ref}
      data-testid={`news-card-${article.id}`}
      drag={depth === 0 ? "y" : false}
      dragElastic={0.35}
      dragMomentum={false}
      {...(dragHandlers || {})}
      initial={initial}
      animate={animate}
      exit={exit}
      transition={transition}
      style={style}
      className="absolute inset-0 will-change-transform"
    >
      <div className="relative w-full h-full overflow-hidden">
        <img
          src={fileUrl(article.image_url)}
          alt={article.title}
          className="absolute inset-0 w-full h-full object-cover"
          draggable={false}
        />
        <div className="absolute inset-0 qd-image-overlay" />
        <div className="absolute inset-0 qd-image-vignette pointer-events-none" />

        {/* Bottom content */}
        <div className="absolute inset-x-0 bottom-0 px-5 sm:px-10 lg:px-20 pb-10 sm:pb-16 lg:pb-20 text-white max-w-3xl">
          <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-5 flex-wrap">
            <span
              data-testid="card-category"
              className="overline px-2.5 sm:px-3 py-1 border border-white/50 rounded-full text-white bg-black/25 backdrop-blur-sm"
            >
              {article.category}
            </span>
            <span className="overline text-white/85">
              {formatDate(article.created_at)}
            </span>
          </div>

          <h1
            data-testid="card-title"
            className="font-serif-display text-3xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-3 sm:mb-5 drop-shadow-[0_2px_18px_rgba(0,0,0,0.5)]"
          >
            {article.title}
          </h1>

          <p
            data-testid="card-description"
            className="font-sans-modern text-sm sm:text-base text-white/90 leading-relaxed mb-6 sm:mb-7 max-w-2xl drop-shadow-[0_1px_8px_rgba(0,0,0,0.5)]"
          >
            {article.description}
          </p>

          <div className="flex items-center justify-between gap-4 sm:gap-6 flex-wrap">
            <Link
              to={`/article/${article.id}`}
              data-testid="read-more-button"
              className="group inline-flex items-center gap-2.5 sm:gap-3 bg-white text-[#1A1A1A] hover:bg-[#D4A373] hover:text-white transition-colors px-5 sm:px-6 py-2.5 sm:py-3 rounded-full font-sans-modern text-xs sm:text-sm tracking-wide shadow-[0_4px_16px_rgba(0,0,0,0.25)]"
            >
              Read the story
              <ArrowUpRight
                size={15}
                strokeWidth={1.8}
                className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </Link>

            <div className="flex items-center gap-3 sm:gap-5 text-[11px] sm:text-xs text-white/85 font-sans-modern">
              <span className="flex items-center gap-1.5">
                <User size={12} strokeWidth={1.5} />
                {article.editor_name}
              </span>
              <span className="hidden sm:flex items-center gap-1.5">
                <Calendar size={12} strokeWidth={1.5} />
                {formatTime(article.created_at)}
              </span>
            </div>
          </div>
        </div>

        {/* Swipe hint */}
        {depth === 0 && (
          <div
            data-testid="swipe-hint"
            className="absolute top-1/2 right-6 sm:right-10 -translate-y-1/2 hidden md:flex flex-col items-center gap-2 text-white/60"
          >
            <span className="overline">Swipe</span>
            <motion.div
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
              className="w-px h-12 bg-white/50"
            />
          </div>
        )}
      </div>
    </motion.article>
  );
});

export default NewsCard;
